<template>
    <div class="modal-overlay" @click="closeModal">
        <div class="image-search-modal-container" @click.stop>
            <div class="image-search-modal-header">
                <div class="image-search-modal-title">
                    <span class="search-icon">🔍</span>
                    이미지 검색 결과
                    <span v-if="searchQuery" class="search-query">"{{ searchQuery }}"</span>
                </div>
                <button class="close-button" @click="closeModal">
                    <span>&times;</span>
                </button>
            </div>

            <div class="image-search-modal-body" :class="{ 'has-footer': selectedImage }">
                <!-- * 검색 섹션 -->
                <div class="search-section">
                    <div class="search-input-container">
                        <input class="search-input" v-model="localSearchQuery" type="text" placeholder="검색어를 입력하세요"
                            @keydown.enter="performSearch" @input="onSearchInput" />
                        <button class="search-button" @click="performSearch" :disabled="isLoading">
                            <span v-if="!isLoading" class="search-icon-button">🔍</span>
                            <span v-else class="loading-spinner">⏳</span>
                        </button>
                    </div>
                    <div class="search-tips">
                        💡 팁: 구체적인 제품명으로 검색하면 더 정확한 결과를 얻을 수 있습니다.
                    </div>
                </div>

                <!-- * 로딩 상태 -->
                <div v-if="isLoading" class="loading-container">
                    <div class="loading-icon">🔄</div>
                    <div class="loading-text">이미지를 검색하고 있습니다...</div>
                </div>

                <!-- * 에러 상태 -->
                <div v-else-if="error" class="error-container">
                    <div class="error-icon">⚠️</div>
                    <h3 class="error-title">검색 중 오류가 발생했습니다</h3>
                    <p class="error-message">{{ error }}</p>
                    <button class="retry-button" @click="performSearch">다시 시도</button>
                </div>

                <!-- * 검색 결과 없음 -->
                <div v-else-if="searchResults.length === 0 && hasSearched" class="empty-container">
                    <div class="empty-icon">📭</div>
                    <h3 class="empty-title">검색 결과가 없습니다</h3>
                    <p class="empty-message">다른 검색어로 시도해보세요.</p>
                </div>

                <!-- * 이미지 그리드 -->
                <div v-else-if="searchResults.length > 0" class="image-grid">
                    <div v-for="image in searchResults" :key="image.id" class="image-card"
                        :class="{ selected: selectedImage?.id === image.id }" @click="selectImage(image)">
                        <div class="image-container">
                            <img class="image" :src="image.thumbnail" :alt="image.title" @error="handleImageError"
                                loading="lazy" />
                        </div>
                        <div class="image-info">
                            <div class="image-title" :title="image.title">{{ truncateTitle(image.title) }}</div>
                            <div class="image-size">{{ image.sizewidth }} × {{ image.sizeheight }}</div>
                        </div>
                    </div>
                </div>

                <!-- * 더 보기 버튼 -->
                <div v-if="searchResults.length > 0 && canLoadMore" class="load-more-container">
                    <button class="load-more-button" @click="loadMore" :disabled="isLoadingMore">
                        <span v-if="!isLoadingMore">더 보기</span>
                        <span v-else>로딩 중...</span>
                    </button>
                </div>
            </div>

            <!-- * Footer -->
            <div v-if="selectedImage" class="modal-footer-container">
                <div class="selected-image-preview">
                    <img class="preview-image" :src="selectedImage.thumbnail" :alt="selectedImage.title" />
                    <div class="preview-info">
                        <div class="preview-title">{{ selectedImage.title }}</div>
                        <div class="preview-size">{{ selectedImage.sizewidth }} × {{ selectedImage.sizeheight }}</div>
                    </div>
                </div>
                <div class="footer-buttons">
                    <button class="confirm-button" @click="confirmSelection">선택 완료</button>
                    <button class="cancel-button" @click="clearSelection">선택 취소</button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'ImageSearchModal',

    props: {
        initialSearchQuery: {
            type: String,
            default: ''
        }
    },

    emits: ['close', 'imageSelected'],

    data() {
        return {
            localSearchQuery: '',
            searchQuery: '',
            searchResults: [],
            selectedImage: null,
            isLoading: false,
            isLoadingMore: false,
            error: null,
            hasSearched: false,
            currentPage: 1,
            canLoadMore: true,
            searchDebounceTimer: null,
            originalBodyOverflow: null
        };
    },

    mounted() {
        // body 스크롤 비활성화 및 원래 상태 저장
        this.originalBodyOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        // 초기 검색어가 있으면 자동 검색
        if (this.initialSearchQuery) {
            this.localSearchQuery = this.initialSearchQuery;
            this.performSearch();
        }

        // ESC 키 이벤트 리스너
        document.addEventListener('keydown', this.handleEscKey, true);
    },

    beforeUnmount() {
        // body 스크롤 상태 복원
        document.body.style.overflow = this.originalBodyOverflow || '';

        document.removeEventListener('keydown', this.handleEscKey, true);
        if (this.searchDebounceTimer) {
            clearTimeout(this.searchDebounceTimer);
        }
    },

    methods: {
        // 검색 수행
        async performSearch() {
            if (!this.localSearchQuery.trim()) {
                this.error = '검색어를 입력해주세요.';
                return;
            }

            try {
                this.isLoading = true;
                this.error = null;
                this.searchResults = [];
                this.selectedImage = null;
                this.currentPage = 1;
                this.searchQuery = this.localSearchQuery.trim();
                this.hasSearched = true;
                this.canLoadMore = true;

                const response = await this.$axios.get(
                    `${this.$apiUrls.hanqmedi}/naver/searchImages`,
                    {
                        params: {
                            query: this.searchQuery,
                            display: 20,
                            start: 1,
                            sort: 'sim'
                        }
                    }
                );

                if (response.data.success) {
                    this.searchResults = response.data.data.images;
                    this.canLoadMore = this.searchResults.length >= 20;
                } else {
                    throw new Error(response.data.message || '검색에 실패했습니다.');
                }

            } catch (error) {
                console.error('이미지 검색 오류:', error);
                this.error = error.response?.data?.message || '이미지 검색 중 오류가 발생했습니다.';
            } finally {
                this.isLoading = false;
            }
        },

        // 더 보기
        async loadMore() {
            if (this.isLoadingMore || !this.canLoadMore) return;

            try {
                this.isLoadingMore = true;
                this.currentPage++;

                const response = await this.$axios.get(
                    `${this.$apiUrls.hanqmedi}/naver/searchImages`,
                    {
                        params: {
                            query: this.searchQuery,
                            display: 20,
                            start: (this.currentPage - 1) * 20 + 1,
                            sort: 'sim'
                        }
                    }
                );

                if (response.data.success) {
                    const newImages = response.data.data.images;
                    this.searchResults.push(...newImages);
                    this.canLoadMore = newImages.length >= 20;
                }

            } catch (error) {
                console.error('더 보기 로드 오류:', error);
                this.$swal.fire('오류', '추가 이미지를 불러오는데 실패했습니다.', 'error');
            } finally {
                this.isLoadingMore = false;
            }
        },

        // 검색어 입력 처리
        onSearchInput() {
            if (this.searchDebounceTimer) {
                clearTimeout(this.searchDebounceTimer);
            }

            this.searchDebounceTimer = setTimeout(() => {
                if (this.localSearchQuery.trim() && this.localSearchQuery.trim() !== this.searchQuery) {
                    this.performSearch();
                }
            }, 500);
        },

        // 이미지 선택
        selectImage(image) {
            this.selectedImage = image;
        },

        // 선택 확인
        confirmSelection() {
            if (this.selectedImage) {
                this.$emit('imageSelected', {
                    url: this.selectedImage.link,
                    thumbnail: this.selectedImage.thumbnail,
                    title: this.selectedImage.title,
                    width: this.selectedImage.sizewidth,
                    height: this.selectedImage.sizeheight
                });
                this.closeModal();
            }
        },

        // 선택 취소
        clearSelection() {
            this.selectedImage = null;
        },

        // 제목 길이 제한
        truncateTitle(title, maxLength = 15) {
            return title.length > maxLength ? title.substring(0, maxLength) + '...' : title;
        },

        // 이미지 로드 오류 처리
        handleImageError(event) {
            event.target.style.display = 'none';
        },

        // ESC 키 처리
        handleEscKey(event) {
            if (event.key === 'Escape') {
                // 이벤트를 즉시 차단
                event.preventDefault();
                event.stopPropagation();
                event.stopImmediatePropagation();

                // 다른 모든 ESC 키 핸들러가 실행되지 않도록 추가 차단
                if (event.cancelable) {
                    event.returnValue = false;
                }

                this.closeModal();
                return false;
            }
        },
        
        // 모달 닫기
        closeModal() {
            this.$emit('close');
        }
    }
};
</script>

<style scoped>
/* 모달 기본 구조 */
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.6);
    display: flex;
    justify-content: center;
    align-items: center;
    backdrop-filter: blur(3px);
    z-index: 9999;
}

.image-search-modal-container {
    background: white;
    border-radius: 20px;
    width: 90vw;
    max-width: 1200px;
    height: 85vh;
    max-height: 800px;
    display: flex;
    flex-direction: column;
    box-shadow: 0 25px 50px -20px rgba(0, 0, 0, 0.25);
    overflow: hidden;
    animation: modalSlideIn 0.3s ease-out;
}

@keyframes modalSlideIn {
    from {
        opacity: 0;
        transform: translateY(-30px) scale(0.95);
    }

    to {
        opacity: 1;
        transform: translateY(0) scale(1);
    }
}

.image-search-modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 24px 32px;
    border-bottom: 1px solid #e9ecef;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
}

.image-search-modal-title {
    margin: 0;
    font-size: 1.5rem;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 12px;
}

.search-icon {
    font-size: 1.5rem;
}

.search-query {
    font-size: 1.1rem;
    color: #e3f2fd;
    font-weight: 400;
}

.close-button {
    background: none;
    border: none;
    font-size: 2rem;
    color: white;
    cursor: pointer;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
}

.close-button:hover {
    background-color: rgba(255, 255, 255, 0.1);
    transform: scale(1.1);
}

.image-search-modal-body {
    flex: 1;
    overflow-y: auto;
    padding: 24px 32px;
    display: flex;
    flex-direction: column;
    gap: 24px;
}

/* footer가 있을 때 패딩 조정 */
.image-search-modal-body.has-footer {
    padding-bottom: 12px;
}

/* 검색 섹션 */
.search-section {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.search-input-container {
    display: flex;
    gap: 12px;
    align-items: center;
}

.search-input {
    flex: 1;
    padding: 14px 20px;
    border: 2px solid #e9ecef;
    border-radius: 50px;
    font-size: 1rem;
    transition: all 0.2s ease;
}

.search-input:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.search-input::placeholder {
    color: #6c757d;
}

.search-button {
    padding: 14px 24px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border: none;
    border-radius: 50px;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    min-width: 100px;
}

.search-button:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(102, 126, 234, 0.3);
}

.search-button:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.search-icon-button {
    font-size: 1.2rem;
}

.loading-spinner {
    font-size: 1.2rem;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    from {
        transform: rotate(0deg);
    }

    to {
        transform: rotate(360deg);
    }
}

.search-tips {
    font-size: 15px;
    color: #6c757d;
    padding: 8px 16px;
    background: #f8f9fa;
    border-radius: 12px;
}

/* 상태별 컨테이너 */
.loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 20px;
    gap: 20px;
}

.loading-icon {
    font-size: 3rem;
    animation: bounce 1s infinite;
}

@keyframes bounce {

    0%,
    20%,
    50%,
    80%,
    100% {
        transform: translateY(0);
    }

    40% {
        transform: translateY(-10px);
    }

    60% {
        transform: translateY(-5px);
    }
}

.loading-text {
    font-size: 1.1rem;
    color: #6c757d;
    font-weight: 500;
}

.error-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 20px;
    gap: 16px;
}

.error-icon {
    font-size: 3rem;
}

.error-title {
    margin: 0;
    font-size: 1.3rem;
    color: #dc3545;
    font-weight: 600;
}

.error-message {
    margin: 0;
    font-size: 1rem;
    color: #6c757d;
    text-align: center;
}

.retry-button {
    padding: 12px 24px;
    background: #dc3545;
    color: white;
    border: none;
    border-radius: 25px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
}

.retry-button:hover {
    background: #c82333;
    transform: translateY(-2px);
}

.empty-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 20px;
    gap: 16px;
}

.empty-icon {
    font-size: 3rem;
}

.empty-title {
    margin: 0;
    font-size: 1.3rem;
    color: #495057;
    font-weight: 600;
}

.empty-message {
    margin: 0;
    font-size: 1rem;
    color: #6c757d;
    text-align: center;
}

/* 이미지 그리드 */
.image-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 20px;
    padding: 10px 0;
}

.image-card {
    border: 2px solid #e9ecef;
    border-radius: 16px;
    overflow: hidden;
    cursor: pointer;
    transition: all 0.3s ease;
    background: white;
}

.image-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 30px rgba(0, 0, 0, 0.15);
    border-color: #667eea;
}

.image-card.selected {
    border-color: #667eea;
    box-shadow: 0 8px 25px rgba(102, 126, 234, 0.25);
    transform: translateY(-2px);
}

.image-container {
    position: relative;
    aspect-ratio: 1;
    overflow: hidden;
}

.image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
}

.image-card:hover .image {
    transform: scale(1.05);
}

.image-info {
    padding: 11px;
}

.image-title {
    font-size: 0.9rem;
    font-weight: 500;
    color: #333;
    margin-bottom: 6px;
}

.image-size {
    font-size: 0.8rem;
    color: #6c757d;
}

/* 더 보기 */
.load-more-container {
    display: flex;
    justify-content: center;
    padding: 20px 0;
}

.load-more-button {
    padding: 14px 32px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border: none;
    border-radius: 50px;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
}

.load-more-button:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(102, 126, 234, 0.3);
}

.load-more-button:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

/* Footer 스타일 */
.modal-footer-container {
    position: relative;
    padding: 24px 32px;
    border-top: 1px solid #e9ecef;
    background: #f8f9fa;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 24px;
    min-height: 50px;
    flex-shrink: 0;
}

.selected-image-preview {
    display: flex;
    align-items: center;
    gap: 16px;
    flex: 1;
}

.preview-image {
    width: 60px;
    height: 60px;
    object-fit: cover;
    border-radius: 12px;
    border: 2px solid #e9ecef;
    flex-shrink: 0;
}

.preview-info {
    display: flex;
    flex-direction: column;
    gap: 4px;
    flex: 1;
    min-width: 0;
}

.preview-title {
    font-size: 0.95rem;
    font-weight: 500;
    color: #333;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.preview-size {
    font-size: 0.85rem;
    color: #6c757d;
}

.footer-buttons {
    display: flex;
    gap: 12px;
    flex-shrink: 0;
}

.confirm-button,
.cancel-button {
    padding: 12px 24px;
    border: none;
    border-radius: 25px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    font-size: 0.95rem;
    min-width: 100px;
    color: white;
}

.confirm-button {
    background: #28a745;
}

.confirm-button:hover {
    background: #218838;
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(40, 167, 69, 0.3);
}

.cancel-button {
    background: #6c757d;
}

.cancel-button:hover {
    background: #5a6268;
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(108, 117, 125, 0.3);
}

/* 반응형 디자인 */
@media (max-width: 768px) {
    .modal-footer-container {
        flex-direction: column;
        gap: 16px;
        padding: 20px;
    }

    .selected-image-preview {
        flex-direction: column;
        text-align: center;
    }

    .preview-title {
        max-width: 100%;
    }

    .footer-buttons {
        width: 100%;
        justify-content: center;
    }

    .image-grid {
        grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
        gap: 15px;
    }

    .image-search-modal-container {
        width: 95vw;
        height: 90vh;
    }

    .image-search-modal-header {
        padding: 20px;
    }

    .image-search-modal-body {
        padding: 20px;
    }
}
</style>